import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  TextField,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import axios from 'axios';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import theme from '../../../theme';
import { openSnackbar } from '../../ErrorMessageSnackbar';

interface DoubleCotationProps {
  deal: any;
  dataStore: any;
  dealId: any;
  cancellationRequested: boolean;
  cancellationDate: string;
  showRefuseButton: boolean;
  dealData: any;
  stageId?: number; // Ajout du stageId pour savoir dans quel stage on est
  onCancellationUpdate: (requested: boolean, date: string) => void;
}

export const DoubleCotation: React.FC<DoubleCotationProps> = ({
  deal,
  dataStore,
  dealId,
  cancellationRequested,
  cancellationDate,
  showRefuseButton,
  dealData,
  stageId,
  onCancellationUpdate,
}) => {
  const navigate = useNavigate();
  const [openConfirmRefuseDialog, setOpenConfirmRefuseDialog] = useState(false);
  const [dcMarketPrice, setDcMarketPrice] = useState<number | ''>('');
  const [dcDesiredPrice, setDcDesiredPrice] = useState<number | ''>('');
  const [estimatedRotationWeeks, setEstimatedRotationWeeks] = useState<number>(1);
  const [currentDataStore, setCurrentDataStore] = useState(null);
  const [currentDealId, setCurrentDealId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const handleOpenSnackbar = openSnackbar(setSnackbarMessage, setSnackbarOpen, setSeverity);

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  const handleApiError = (error: any, context: string) => {
    Sentry.withScope((scope) => {
      scope.setTag('where', context);
      scope.setExtra('deal', deal);

      if (axios.isAxiosError(error)) {
        if (error.response) {
          scope.setExtra('response', {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          });
          handleOpenSnackbar(`Erreur serveur: ${error.response.status}`, 'error');
        } else if (error.request) {
          scope.setExtra('request', error.request);
          handleOpenSnackbar('Erreur réseau ou serveur non disponible', 'error');
        } else {
          handleOpenSnackbar('Erreur inattendue lors de la communication avec le serveur', 'error');
        }
      } else {
        handleOpenSnackbar('Une erreur inattendue est survenue', 'error');
      }

      Sentry.captureException(error);
    });
  };

  const callSparkCreateAPI = async (button: string, dataStore: any, dealId: any) => {
    const url = '/spark/create/';
    const data = {
      type: 'cotation_response',
      t_s_key: 'SVEHeNoxB4bjTKDjnHqJi15L1fc6MRBYwV1lBqr8NjCZhvYCGP',
      system: 'skipcar',
      data_store: dataStore,
      data: {
        response_val: button,
      },
    };
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };

    try {
      const response = await axios.post(url, data, config);

      if (response.data.response.valid) {
        const nextStage = (button: string) => {
          if (button == 'accept') return 30;
          if (button == 'cancel') return 1;
          if (button == 'refuse') return 14;
          return 14;
        };

        const dateFieldToUse = (button: string) => {
          if (button == 'accept') return 'f1527a798f3678aa2b7c2491a2f52b2a81d42b9e';
          if (button == 'cancel') return '13d1285980d957f7b41f60e6ddbc1ed7b6713545';
          if (button == 'refuse') return 'f1527a798f3678aa2b7c2491a2f52b2a81d42b9e';
          return '';
        };
        const fieldToUpdate = dateFieldToUse(button);
        const formattedDate = button == 'refuse' ? '' : `${year}-${month}-${day}`;

        makeAuthenticatedApiCall('patch', `/api/pipedrive/deals/${dealId}`, {
          stage_id: nextStage(button),
          [fieldToUpdate]: formattedDate,
        })
          .then(() => {})
          .catch((error) => {
            console.error(error);
          });
      }
    } catch (error) {
      console.error(`Erreur lors de l'envoi de la requête : ${error}`);
    }
  };

  const handleRefuseButtonClick = async (
    event: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    _button: string,
    dataStore: any,
    dealId: any,
  ) => {
    event.stopPropagation();

    setCurrentDataStore(dataStore);
    setCurrentDealId(dealId);
    setOpenConfirmRefuseDialog(true);
  };

  const confirmRefusal = async (dcMarketPrice?: number, dcDesiredPrice?: number, rotationWeeks?: number) => {
    setIsProcessing(true);

    try {
      // Appel API pour la date de refus
      const response = await makeAuthenticatedApiCall('post', `/api/deals/${currentDealId}/refusal-date`);
      if (response.status === 200) {
        await callSparkCreateAPI('refuse', currentDataStore, currentDealId);
      }

      // Appel API pour créer la demande de cofinancement
      if (dcMarketPrice) {
        const cofinancementResponse = await makeAuthenticatedApiCall('post', `/api/cofinancement`, {
          deal_id: currentDealId,
          estimated_selling_price: dcMarketPrice,
          client_desired_price: dcDesiredPrice,
          estimated_rotation_weeks: rotationWeeks || 1,
        });
        console.log(cofinancementResponse.data);

        // Rediriger vers la page de cofinancement après création réussie
        navigate(`/cofinancement/${currentDealId}`);
      }

      // Succès - fermer la modale et mettre à jour l'état
      setOpenConfirmRefuseDialog(false);
      const refusalDate = new Date().toLocaleDateString('fr-FR');
      onCancellationUpdate(true, refusalDate);

      // Réinitialiser les champs
      setDcMarketPrice('');
      setDcDesiredPrice('');
      setEstimatedRotationWeeks(1);
    } catch (error) {
      handleApiError(error, 'confirmRefusal');
    } finally {
      setIsProcessing(false);
    }
  };

  // Stages de A1 à A6 : 14, 30, 32, 24, 33, 15
  const isStageA1ToA6 = stageId && [14, 30, 32, 24, 33, 15].includes(stageId);
  const isStageA1 = stageId === 14;

  return (
    <>
      {/* Bouton "Demande de cofinancement" - uniquement visible en A1 (stage 14) et si pas de demande en cours */}
      {!cancellationRequested && !dealData?.sender && isStageA1 ? (
        <Fade in={showRefuseButton}>
          <Button
            variant="contained"
            onClick={(event) => handleRefuseButtonClick(event, 'refuse', dataStore, dealId)}
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: theme.palette.saffron.main,
              '&:hover': { backgroundColor: theme.palette.saffron.main },
            }}
            id={`refuse-${dataStore}`}
          >
            Skipcar+
          </Button>
        </Fade>
      ) : null}

      {/* Message et bouton "Voir la demande" - visible de A1 à A6 si une demande existe */}
      {cancellationRequested && !dealData?.sender && isStageA1ToA6 && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography align="center" sx={{ fontSize: 14 }} variant="body2">
            Skipcar+ demandé le {cancellationDate}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/cofinancement/${dealId}`);
            }}
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            Voir Skipcar+
          </Button>
        </Box>
      )}

      <Dialog
        open={openConfirmRefuseDialog}
        onClose={isProcessing ? undefined : () => setOpenConfirmRefuseDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableEscapeKeyDown={isProcessing}
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Typography variant="body1">Vous souhaitez tester Skipcar+ ?</Typography>
            <Typography variant="body2" sx={{ marginTop: 2 }}>
              Veuillez entrer le prix de vente estimé, le prix désiré par le client et la rotation estimée.
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ marginTop: 2 }}>
              <TextField
                margin="dense"
                className="inputElement"
                id="dcDesiredPrice"
                label="Prix désiré par le client"
                type="number"
                fullWidth
                value={dcDesiredPrice}
                onChange={(e) => setDcDesiredPrice(e.target.value === '' ? '' : Number(e.target.value))}
              />
              <TextField
                autoFocus
                margin="dense"
                className="inputElement"
                id="dcMarketPrice"
                label="Prix de vente estimé"
                required
                sx={{ marginRight: 2 }}
                type="number"
                fullWidth
                value={dcMarketPrice}
                onChange={(e) => setDcMarketPrice(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Box>
            <Box sx={{ marginTop: 2 }}>
              <TextField
                margin="dense"
                className="inputElement"
                id="estimatedRotationWeeks"
                label="Rotation estimée (semaines)"
                required
                type="number"
                inputProps={{ min: 1, max: 24 }}
                fullWidth
                value={estimatedRotationWeeks}
                onChange={(e) => setEstimatedRotationWeeks(Number(e.target.value))}
                helperText="Nombre de semaines estimées pour la rotation du véhicule (1-24)"
              />
              {/*deal?.encheres_vo_price && parseFloat(deal.encheres_vo_price.replace(/[^\d.-]/g, '')) > 13000 && (
                <Typography variant="body2" sx={{ color: 'red', marginTop: 1, fontWeight: 'bold' }}>
                  ⚠️ Attention SKIPCAR vous demandera un CT de - de 6 mois le jour de la vente
                </Typography>
              )}*/}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className="secondaryDealActionButton"
            onClick={() => setOpenConfirmRefuseDialog(false)}
            disabled={isProcessing}
          >
            Annuler
          </Button>
          <Button
            disabled={dcMarketPrice === '' || estimatedRotationWeeks < 1 || estimatedRotationWeeks > 24 || isProcessing}
            className="dealActionButton"
            onClick={() => {
              confirmRefusal(dcMarketPrice || undefined, dcDesiredPrice || undefined, estimatedRotationWeeks);
            }}
            autoFocus
            startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isProcessing ? 'Traitement en cours...' : 'Je fais une simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
